var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/utils/transcription/mime-detection.ts
function detectAudioMimeType(buffer) {
  if (buffer.length < 4) return "audio/ogg";
  if (buffer.length >= 8 && buffer[4] === 102 && buffer[5] === 116 && buffer[6] === 121 && buffer[7] === 112) {
    return "audio/mp4";
  }
  let offset = 0;
  if (buffer[0] === 73 && buffer[1] === 68 && buffer[2] === 51) {
    if (buffer.length >= 10) {
      const size = (buffer[6] & 127) << 21 | (buffer[7] & 127) << 14 | (buffer[8] & 127) << 7 | buffer[9] & 127;
      offset = 10 + size;
      if (offset >= buffer.length) offset = 0;
    }
  }
  if (buffer.length - offset >= 4) {
    if (buffer[offset] === 255 && (buffer[offset + 1] === 251 || buffer[offset + 1] === 250) || buffer[offset] === 255 && buffer[offset + 1] === 243 || buffer[offset] === 255 && buffer[offset + 1] === 242) {
      return "audio/mpeg";
    }
    if (buffer[offset] === 79 && buffer[offset + 1] === 103 && buffer[offset + 2] === 103 && buffer[offset + 3] === 83) {
      return "audio/ogg";
    }
    if (buffer[offset] === 82 && buffer[offset + 1] === 73 && buffer[offset + 2] === 70 && buffer[offset + 3] === 70) {
      return "audio/wav";
    }
    if (buffer[offset] === 102 && buffer[offset + 1] === 76 && buffer[offset + 2] === 97 && buffer[offset + 3] === 67) {
      return "audio/flac";
    }
  }
  return "audio/ogg";
}
function extensionForMimeType(mimeType) {
  switch (mimeType) {
    case "audio/mpeg":
      return "mp3";
    case "audio/wav":
      return "wav";
    case "audio/flac":
      return "flac";
    case "audio/mp4":
      return "mp4";
    case "audio/ogg":
    default:
      return "ogg";
  }
}

// src/utils/transcription/providers.ts
var fileReader;
function registerFileReader(reader) {
  fileReader = reader;
}
async function readAudioPath(audioPath) {
  if (!fileReader) {
    throw new Error(
      "audioPath input is not available in this runtime \u2014 pass audioBuffer/audioUrl instead, or import '@wovin/tranz/node' to enable file paths"
    );
  }
  return await fileReader(audioPath);
}
function arrayBufferToBase64(buffer) {
  if (typeof btoa !== "undefined") {
    const binary = Array.from(buffer).map((byte) => String.fromCharCode(byte)).join("");
    return btoa(binary);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(buffer).toString("base64");
  }
  throw new Error("No base64 encoding available in this runtime");
}
async function toAudioBlob(input, explicitMimeType) {
  if (input instanceof Blob) {
    const mimeType2 = explicitMimeType || input.type || detectAudioMimeType(
      new Uint8Array(await input.slice(0, 4096).arrayBuffer())
    );
    return {
      blob: input.type === mimeType2 ? input : new Blob([input], { type: mimeType2 }),
      mimeType: mimeType2
    };
  }
  const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
  const mimeType = explicitMimeType || detectAudioMimeType(bytes);
  return {
    blob: new Blob([new Uint8Array(bytes)], { type: mimeType }),
    mimeType
  };
}
function createProvider(providerName, config) {
  switch (providerName) {
    case "whisper":
      throw new Error(
        "WhisperProvider is Node-only \u2014 import { createProvider } from '@wovin/tranz/node'"
      );
    case "mistral":
      return new MistralProvider();
    case "greenpt":
      return new GreenPTProvider();
    case "mimo":
      return new MimoProvider();
    default:
      throw new Error(`Unknown provider: ${providerName}`);
  }
}
var VOXTRAL_LIMITS = {
  /** Maximum audio duration in seconds (3 hours for Voxtral Transcribe 2) */
  maxAudioDurationSec: 3 * 60 * 60,
  // 10800 seconds = 3 hours
  /** Maximum context biasing words/phrases */
  maxContextBiasingTerms: 100,
  /** Nominal maximum file size in bytes (1GB) — the documented ceiling, NOT reliably accepted. */
  maxFileSizeBytes: 1024 * 1024 * 1024,
  /**
   * Practical safe upload size in bytes for the /audio/transcriptions endpoint.
   *
   * Request payload size is a hard ceiling independent of audio duration. Measured against
   * the live API (voxtral-mini-latest): 98MB ✅, 123MB ✅, 152MB ❌ 503, and a full 163min
   * file at 14MB ✅ — so the cliff is ~123-152MB regardless of length. 120MB sits just under
   * the proven-good 123MB with ~20% margin below the 152MB failure. Used to auto-split
   * oversized inputs into byte-bounded segments.
   */
  recommendedMaxUploadBytes: 120 * 1024 * 1024
};
var MistralProvider = class {
  constructor() {
    __publicField(this, "name", "mistral");
    __publicField(this, "maxAudioDurationSec", VOXTRAL_LIMITS.maxAudioDurationSec);
  }
  async transcribe(params) {
    if (params.language && params.timestampGranularity) {
      throw new Error(
        "Cannot use both language and timestampGranularity (Mistral API limitation)"
      );
    }
    const diarize = params.diarize ?? (params.timestampGranularity === "word" ? false : true);
    if (diarize && params.timestampGranularity === "word") {
      throw new Error(
        'When diarize is set to true, the timestamp granularity must be set to ["segment"], got ["word"]'
      );
    }
    const formData = new FormData();
    if (params.audioUrl) {
      formData.append("file_url", params.audioUrl);
    } else {
      let input = params.audioBuffer;
      if (!input && params.audioPath) {
        input = await readAudioPath(params.audioPath);
      }
      if (!input) {
        return {
          text: "",
          error: "No audio input provided (audioPath, audioBuffer, or audioUrl required)"
        };
      }
      const { blob, mimeType } = await toAudioBlob(input, params.mimeType);
      formData.append("file", blob, `audio.${extensionForMimeType(mimeType)}`);
    }
    const model = params.model || "voxtral-mini-latest";
    formData.append("model", model);
    if (params.language) {
      formData.append("language", params.language);
    }
    if (diarize) {
      formData.append("diarize", "true");
    }
    const timestampGranularity = params.language ? void 0 : params.timestampGranularity ?? "segment";
    if (timestampGranularity) {
      formData.append("timestamp_granularities", timestampGranularity);
    }
    if (params.contextBias && params.contextBias.length > 0) {
      if (params.contextBias.length > VOXTRAL_LIMITS.maxContextBiasingTerms) {
        throw new Error(
          `contextBias has ${params.contextBias.length} terms; Voxtral limit is ${VOXTRAL_LIMITS.maxContextBiasingTerms}`
        );
      }
      for (const term of params.contextBias) {
        formData.append("context_bias[]", term);
      }
    }
    const response = await fetch(
      "https://api.mistral.ai/v1/audio/transcriptions",
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${params.apiKey}`
        },
        body: formData
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      return {
        text: "",
        error: `API returned ${response.status}: ${errorText}`
      };
    }
    const result = await response.json();
    if (!result?.text) {
      return {
        text: "",
        error: "No transcription returned",
        rawResponse: result
      };
    }
    const segments = Array.isArray(result.segments) && result.segments.length > 0 ? result.segments.map((seg) => ({
      startMs: Math.round((seg.start ?? 0) * 1e3),
      endMs: Math.round((seg.end ?? 0) * 1e3),
      text: seg.text ?? "",
      ...seg.speaker_id !== void 0 ? { diarization: seg.speaker_id } : {}
    })) : void 0;
    let words;
    if (Array.isArray(result.words) && result.words.length > 0) {
      words = result.words;
    } else if (Array.isArray(result.segments)) {
      const nested = result.segments.flatMap((seg) => seg.words ?? []);
      if (nested.length > 0) words = nested;
    }
    const duration = result.usage?.prompt_audio_seconds;
    return {
      text: result.text,
      language: result.language ?? params.language,
      model: result.model,
      duration,
      ...words ? { words } : {},
      ...segments ? { segments } : {},
      rawResponse: result
    };
  }
};
var GreenPTProvider = class {
  constructor() {
    __publicField(this, "name", "greenpt");
  }
  async transcribe(params) {
    if (!params.apiKey) {
      return { text: "", error: "API key is required for GreenPT provider" };
    }
    if (!params.audioPath && !params.audioBuffer) {
      return {
        text: "",
        error: "Audio input is required (audioPath or audioBuffer)"
      };
    }
    try {
      const input = params.audioBuffer ?? await readAudioPath(params.audioPath);
      const formData = new FormData();
      const { blob, mimeType } = await toAudioBlob(input, params.mimeType);
      formData.append("file", blob, `audio.${extensionForMimeType(mimeType)}`);
      const queryParams = new URLSearchParams();
      const model = params.model || "green-s-pro";
      queryParams.append("model", model);
      if (params.language) {
        queryParams.append("language", params.language);
      }
      if (params.diarize !== void 0) {
        queryParams.append("diarize", String(params.diarize));
      }
      queryParams.append("punctuate", "true");
      const url = `https://api.greenpt.ai/v1/listen?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${params.apiKey}`
        },
        body: formData
      });
      if (!response.ok) {
        const errorText = await response.text();
        return {
          text: "",
          error: `API returned ${response.status}: ${errorText}`
        };
      }
      const result = await response.json();
      const transcript = result?.results?.channels?.[0]?.alternatives?.[0];
      if (!transcript) {
        return {
          text: "",
          error: "No transcription returned",
          rawResponse: result
        };
      }
      let text = transcript.transcript;
      if (!text && transcript.words && transcript.words.length > 0) {
        text = transcript.words.map((w) => w.word).join(" ");
      }
      return {
        text: text || "",
        confidence: transcript.confidence,
        words: transcript.words,
        duration: result?.metadata?.duration,
        rawResponse: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        text: "",
        error: `Transcription failed: ${errorMessage}`
      };
    }
  }
};
var _MimoProvider = class _MimoProvider {
  constructor() {
    __publicField(this, "name", "mimo");
  }
  async transcribe(params) {
    if (!params.apiKey) {
      return { text: "", error: "API key is required for Mimo provider" };
    }
    if (!params.audioPath && !params.audioBuffer && !params.audioUrl) {
      return {
        text: "",
        error: "Audio input is required (audioPath, audioBuffer, or audioUrl)"
      };
    }
    try {
      let input = params.audioBuffer;
      if (!input && params.audioPath) {
        input = await readAudioPath(params.audioPath);
      }
      if (!input && params.audioUrl) {
        const urlResp = await fetch(params.audioUrl);
        if (!urlResp.ok) {
          return {
            text: "",
            error: `Failed to fetch audioUrl: ${urlResp.status} ${urlResp.statusText}`
          };
        }
        input = new Uint8Array(await urlResp.arrayBuffer());
      }
      if (!input) {
        return { text: "", error: "No audio input could be resolved" };
      }
      const bytes = input instanceof Blob ? new Uint8Array(await input.arrayBuffer()) : input instanceof ArrayBuffer ? new Uint8Array(input) : input;
      const mimeType = params.mimeType || detectAudioMimeType(bytes);
      if (!_MimoProvider.SUPPORTED_MIME_TYPES.includes(mimeType)) {
        return {
          text: "",
          error: `Mimo ASR supports wav/mp3 only (got ${mimeType}). Convert the audio and retry.`
        };
      }
      const base64Audio = arrayBufferToBase64(bytes);
      const dataUri = `data:${mimeType};base64,${base64Audio}`;
      const body = {
        model: params.model || _MimoProvider.DEFAULT_MODEL,
        messages: [
          {
            role: "user",
            content: [
              {
                type: "input_audio",
                input_audio: { data: dataUri }
              }
            ]
          }
        ],
        asr_options: {
          // Mimo defaults to auto detection when omitted; honor an explicit choice.
          language: params.language ?? "auto"
        }
      };
      const response = await fetch(_MimoProvider.ENDPOINT, {
        method: "POST",
        headers: {
          "api-key": params.apiKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok) {
        const errorText = result?.error?.message || result?.message || JSON.stringify(result);
        return {
          text: "",
          error: `API returned ${response.status}: ${errorText}`,
          rawResponse: result
        };
      }
      const text = result?.choices?.[0]?.message?.content ?? "";
      if (!text) {
        return {
          text: "",
          error: "No transcription returned",
          rawResponse: result
        };
      }
      return {
        text,
        language: params.language ?? "auto",
        model: result.model,
        rawResponse: result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      return {
        text: "",
        error: `Transcription failed: ${errorMessage}`
      };
    }
  }
};
/** Mimo's REST endpoint (NOT the OpenAI base_url shape — header auth is `api-key`). */
__publicField(_MimoProvider, "ENDPOINT", "https://api.xiaomimimo.com/v1/chat/completions");
/** Default ASR model. */
__publicField(_MimoProvider, "DEFAULT_MODEL", "mimo-v2.5-asr");
/** MIME types Mimo accepts. */
__publicField(_MimoProvider, "SUPPORTED_MIME_TYPES", ["audio/wav", "audio/mpeg", "audio/mp3"]);
var MimoProvider = _MimoProvider;

// src/utils/transcription/transcribe-blob.ts
async function transcribeBlob(audio, options) {
  return await transcribeWith({ audioBuffer: audio }, options);
}
async function transcribeUrl(audioUrl, options) {
  return await transcribeWith({ audioUrl }, options);
}
async function transcribeWith(input, options) {
  const {
    apiKey,
    model = "voxtral-mini-latest",
    language,
    diarize = true,
    contextBias,
    mimeType
  } = options;
  const timestamps = options.timestamps ?? (language ? void 0 : "segment");
  const provider = new MistralProvider();
  return await provider.transcribe({
    ...input,
    mimeType,
    apiKey,
    model,
    language,
    diarize,
    timestampGranularity: timestamps,
    contextBias
  });
}

// src/utils/audio/merge-results.ts
function prefixChunkLabel(chunkIndex, value) {
  if (value === void 0) return void 0;
  return `chunk${chunkIndex}/${String(value)}`;
}
function mergeTranscriptionResults(results, segments) {
  if (results.length === 0) {
    return {
      text: "",
      error: "No results to merge"
    };
  }
  if (results.length === 1) {
    return results[0];
  }
  const errors = results.map((r, i) => r.error ? `Segment ${i}: ${r.error}` : null).filter(Boolean);
  if (errors.length > 0) {
    return {
      text: "",
      error: `Errors in segments: ${errors.join("; ")}`
    };
  }
  const mergedText = results.map((r) => r.text.trim()).join("\n\n");
  const mergedWords = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const segment = segments[i];
    const words = result.words || result.rawResponse?.words || [];
    for (const word of words) {
      mergedWords.push({
        word: word.word || word.text,
        start: (word.start || 0) + segment.startSec,
        end: (word.end || 0) + segment.startSec,
        confidence: word.confidence,
        speaker: prefixChunkLabel(i, word.speaker)
      });
    }
  }
  const mergedSegments = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const chunkOffsetMs = Math.round(segments[i].startSec * 1e3);
    if (!result.segments) continue;
    for (const seg of result.segments) {
      mergedSegments.push({
        startMs: seg.startMs + chunkOffsetMs,
        endMs: seg.endMs + chunkOffsetMs,
        text: seg.text,
        ...seg.diarization !== void 0 ? { diarization: prefixChunkLabel(i, seg.diarization) } : {}
      });
    }
  }
  const totalDuration = segments.reduce((sum, seg) => sum + seg.durationSec, 0);
  const audioChunks = results.map((r, i) => ({
    index: i,
    startSec: segments[i].startSec,
    endSec: segments[i].endSec,
    text: r.text.trim()
  }));
  const mergedRawResponse = {
    merged: true,
    chunkCount: results.length,
    chunks: results.map((r, i) => ({
      index: i,
      startSec: segments[i].startSec,
      rawResponse: r.rawResponse
    }))
  };
  const firstResult = results[0];
  return {
    text: mergedText,
    duration: totalDuration,
    language: firstResult.language,
    model: firstResult.model,
    rawResponse: mergedRawResponse,
    audioChunks,
    ...mergedWords.length > 0 ? { words: mergedWords } : {},
    ...mergedSegments.length > 0 ? { segments: mergedSegments } : {}
  };
}
function formatMergedText(result, includeMarkers = false) {
  if (!result.audioChunks || result.audioChunks.length <= 1) {
    return result.text;
  }
  if (!includeMarkers) {
    return result.text;
  }
  return result.audioChunks.map((chunk, i) => {
    const timeStr = formatTimestamp(chunk.startSec);
    return `[Chunk ${i + 1} @ ${timeStr}]
${chunk.text}`;
  }).join("\n\n");
}
function formatTimestamp(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

// src/utils/transcription/format.ts
function formatTranscriptWithPauses(transcript, words, shortPauseThreshold = 1, longPauseThreshold = 5) {
  if (!words || words.length === 0) {
    return transcript;
  }
  const pausePositions = /* @__PURE__ */ new Map();
  for (let i = 0; i < words.length - 1; i++) {
    const word = words[i];
    const nextWord = words[i + 1];
    const gap = nextWord.start - word.end;
    if (gap >= longPauseThreshold) {
      pausePositions.set(i, "long");
    } else if (gap >= shortPauseThreshold) {
      pausePositions.set(i, "short");
    }
  }
  if (pausePositions.size === 0) {
    return transcript;
  }
  const transcriptWords = transcript.split(/\s+/);
  const result = [];
  for (let i = 0; i < transcriptWords.length; i++) {
    result.push(transcriptWords[i]);
    const pauseType = pausePositions.get(i);
    if (pauseType === "long") {
      result.push("\n\n");
    } else if (pauseType === "short") {
      result.push("\n");
    } else if (i < transcriptWords.length - 1) {
      result.push(" ");
    }
  }
  return result.join("");
}
function formatTime(seconds, useHours) {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor(total % 3600 / 60);
  const s = total % 60;
  if (useHours) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}
function formatDurationHuman(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor(total % 3600 / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
function formatTranscriptAsMarkdown(result, opts = {}) {
  const gapSec = opts.gapSec ?? 1.5;
  const wantSpeakerLabel = opts.speakerLabel ?? true;
  const includeHeader = opts.includeHeader ?? false;
  const gapMs = gapSec * 1e3;
  const segments = result.segments ?? [];
  const totalDurationSec = opts.durationSec ?? result.duration ?? (segments.length > 0 ? segments[segments.length - 1].endMs / 1e3 : 0);
  const useHours = totalDurationSec >= 3600;
  const speakerSet = /* @__PURE__ */ new Set();
  for (const seg of segments) {
    if (seg.diarization !== void 0 && seg.diarization !== null) {
      speakerSet.add(seg.diarization);
    }
  }
  const hasMultipleSpeakers = speakerSet.size > 1;
  const showSpeakers = wantSpeakerLabel && hasMultipleSpeakers;
  let headerBlock = "";
  if (includeHeader) {
    const bullets = [];
    if (opts.source) bullets.push(`- Source: \`${opts.source}\``);
    if (totalDurationSec > 0) bullets.push(`- Duration: ${formatDurationHuman(totalDurationSec)}`);
    if (result.model) bullets.push(`- Model: ${result.model}`);
    if (segments.length > 0) bullets.push(`- Segments: ${segments.length}`);
    if (speakerSet.size > 0) bullets.push(`- Speakers (diarized): ${speakerSet.size}`);
    const parts = [];
    if (opts.source) parts.push(`# ${opts.source}`);
    if (bullets.length > 0) parts.push(bullets.join("\n"));
    parts.push("## Transcript");
    headerBlock = parts.join("\n\n");
  }
  const body = [];
  if (segments.length === 0) {
    const text = (result.text ?? "").trim();
    if (text) body.push(text);
  } else {
    const paragraphs = [];
    let current;
    for (const seg of segments) {
      const text = (seg.text ?? "").trim();
      if (!text) continue;
      const gap = current ? seg.startMs - current.lastEndMs : Infinity;
      const speakerChanged = current ? current.speaker !== seg.diarization : false;
      if (!current || gap >= gapMs || speakerChanged) {
        current = {
          startMs: seg.startMs,
          lastEndMs: seg.endMs,
          speaker: seg.diarization,
          texts: [text]
        };
        paragraphs.push(current);
      } else {
        current.texts.push(text);
        current.lastEndMs = seg.endMs;
      }
    }
    for (const p of paragraphs) {
      const time = formatTime(p.startMs / 1e3, useHours);
      const speakerSuffix = showSpeakers && p.speaker !== void 0 && p.speaker !== null ? ` \xB7 Speaker ${formatSpeakerLabel(p.speaker)}` : "";
      body.push(`**[${time}${speakerSuffix}]** ${p.texts.join(" ")}`);
    }
  }
  const bodyBlock = body.join("\n\n");
  const out = [headerBlock, bodyBlock].filter((s) => s.length > 0).join("\n\n");
  return out.trimEnd() + (out.length > 0 ? "\n" : "");
}
function formatSpeakerLabel(value) {
  const s = String(value);
  return s.replace(/^speaker[_-]/, "");
}

// src/utils/synthesis/providers.ts
function rethrowIfUnixSocketDenied(err) {
  const message = String(
    err?.cause ?? err?.message ?? err
  );
  if (/unix domain socket/i.test(message) && /operation not permitted/i.test(message)) {
    throw new Error(
      `Network request failed: creating a Unix domain socket was denied ("cannot create Unix domain socket: Operation not permitted"). On glibc, hostname resolution tries to reach nscd via a Unix socket at /var/run/nscd/socket before falling back to DNS \u2014 if that's being blocked, the request fails without ever getting to Mistral's API. This is typically caused by a process sandbox/seccomp policy restricting AF_UNIX sockets; check whatever sandboxing is wrapping this process (e.g. its Unix-socket allowlist) if that's in play.`,
      { cause: err }
    );
  }
  throw err;
}
function isVoiceIdLike(value) {
  return /^[a-z]{2}_[a-z0-9_]+$/i.test(value) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );
}
var MISTRAL_TTS_DEFAULTS = {
  model: "voxtral-mini-tts-2603",
  apiBase: "https://api.mistral.ai/v1"
};
var MistralSynthesisProvider = class {
  constructor() {
    __publicField(this, "name", "mistral");
  }
  async fetchVoices(apiKey, query = "") {
    const res = await fetch(
      `${MISTRAL_TTS_DEFAULTS.apiBase}/audio/voices${query}`,
      { headers: { Authorization: `Bearer ${apiKey}` } }
    ).catch(rethrowIfUnixSocketDenied);
    if (!res.ok) {
      throw new Error(
        `Failed to list voices: ${res.status} ${await res.text()}`
      );
    }
    const data = await res.json();
    return Array.isArray(data) ? data : data.items ?? [];
  }
  /** Lists the account's own (cloned) voices. */
  async listAccountVoices(apiKey) {
    const items = await this.fetchVoices(apiKey);
    return items.map((v) => ({
      id: v.id,
      name: v.name,
      gender: v.gender,
      description: v.description,
      isPreset: false
    }));
  }
  /** Lists Mistral's built-in preset voices (e.g. `gb_jane_curious`). */
  async listPresetVoices(apiKey) {
    const items = await this.fetchVoices(apiKey, "?type=preset&limit=100");
    return items.map((v) => ({
      id: v.slug ?? v.id,
      name: v.name,
      gender: v.gender,
      languages: v.languages,
      tags: v.tags,
      isPreset: true
    }));
  }
  /** Lists both account voices and built-in presets. */
  async listVoices(apiKey) {
    const [account, presets] = await Promise.all([
      this.listAccountVoices(apiKey),
      this.listPresetVoices(apiKey)
    ]);
    return [...account, ...presets];
  }
  /**
   * Resolve the effective voice id from `voiceId`/`voice`, falling back to the
   * first available account voice. Matches against both account voices and
   * presets: exact slug/id match first, then exact name match, then a fuzzy
   * name match. If nothing matches but the value looks like a provider voice
   * id (slug or UUID), it is passed through as-is rather than rejected.
   */
  async resolveVoiceId(params) {
    if (params.voiceId) return params.voiceId;
    const voices = await this.listVoices(params.apiKey);
    if (voices.length === 0 && !params.voice) {
      throw new Error(
        "No voices available on this Mistral account. Create/save a voice first, or pass an explicit voice id."
      );
    }
    if (params.voice) {
      const wanted = params.voice.trim();
      const wantedLower = wanted.toLowerCase();
      const match = voices.find((v) => v.id.toLowerCase() === wantedLower) ?? voices.find((v) => v.name.toLowerCase() === wantedLower) ?? voices.find((v) => v.name.toLowerCase().includes(wantedLower));
      if (match) return match.id;
      if (isVoiceIdLike(wanted)) return wanted;
      const names = voices.map((v) => `"${v.name}"`).join(", ");
      throw new Error(
        `No voice matching "${params.voice}". Available: ${names}`
      );
    }
    return voices.find((v) => !v.isPreset)?.id ?? voices[0].id;
  }
  async synthesize(params) {
    const model = params.model ?? MISTRAL_TTS_DEFAULTS.model;
    const format = params.responseFormat ?? "mp3";
    const voiceId = await this.resolveVoiceId(params);
    const res = await fetch(`${MISTRAL_TTS_DEFAULTS.apiBase}/audio/speech`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${params.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model,
        input: params.input,
        voice_id: voiceId,
        response_format: format
      })
    }).catch(rethrowIfUnixSocketDenied);
    if (!res.ok) {
      throw new Error(
        `Mistral TTS returned ${res.status}: ${await res.text()}`
      );
    }
    const payload = await res.json();
    if (typeof payload.audio_data !== "string") {
      throw new Error(
        `Unexpected Mistral TTS response (no audio_data): ${JSON.stringify(payload).slice(0, 200)}`
      );
    }
    return {
      audio: Buffer.from(payload.audio_data, "base64"),
      format,
      model,
      voiceId
    };
  }
};
function createSynthesisProvider(name = "mistral") {
  switch (name) {
    case "mistral":
      return new MistralSynthesisProvider();
    default:
      throw new Error(`Unknown synthesis provider: ${name}`);
  }
}

// src/utils/synthesis/text.ts
var DEFAULT_CHUNK_WORDS = 90;
function cleanMarkdown(md) {
  const lines = md.split(/\r?\n/).filter((l) => !l.trimStart().startsWith("#"));
  const text = lines.join("\n");
  return text.replace(/[*_`]/g, "").trim();
}
function splitSentences(text) {
  return text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter((s) => s.length > 0);
}
function chunkText(text, maxWords = DEFAULT_CHUNK_WORDS) {
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.replace(/\s+/g, " ").trim()).filter((p) => p.length > 0);
  const chunks = [];
  let buf = [];
  let count = 0;
  const flush = () => {
    if (buf.length > 0) {
      chunks.push(buf.join(" "));
      buf = [];
      count = 0;
    }
  };
  const addUnit = (unit) => {
    const words = countWords(unit);
    if (buf.length > 0 && count + words > maxWords) flush();
    buf.push(unit);
    count += words;
  };
  for (const paragraph of paragraphs) {
    if (countWords(paragraph) <= maxWords) {
      addUnit(paragraph);
    } else {
      flush();
      for (const sentence of splitSentences(paragraph)) addUnit(sentence);
    }
  }
  flush();
  return chunks;
}
function countWords(text) {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
}
export {
  DEFAULT_CHUNK_WORDS,
  GreenPTProvider,
  MISTRAL_TTS_DEFAULTS,
  MimoProvider,
  MistralProvider,
  MistralSynthesisProvider,
  VOXTRAL_LIMITS,
  chunkText,
  cleanMarkdown,
  countWords,
  createProvider,
  createSynthesisProvider,
  detectAudioMimeType,
  extensionForMimeType,
  formatMergedText,
  formatTranscriptAsMarkdown,
  formatTranscriptWithPauses,
  mergeTranscriptionResults,
  registerFileReader,
  splitSentences,
  transcribeBlob,
  transcribeUrl
};
